import { bookServiceApi } from '@/services/api';

export interface ChapterReviewOption {
  option_id: number;
  text: string;
}

export interface ChapterReviewQuestion {
  id: string;
  section_id: number;
  text: string;
  difficulty: 'easy' | 'medium' | 'hard';
  correct_option_id: number;
  options: ChapterReviewOption[];
}

export interface ChapterReviewSection {
  section_id: number;
  section_number: number;
  title: string;
  summary: string;
  accuracy: number; // 0-100
  question_count: number;
}

export interface ChapterReviewNovelParagraphReaction {
  reaction: string;
  count: number;
  percentage: number;
}

export interface ChapterReviewNovelParagraph {
  index: number;
  content: string;
  reactions: ChapterReviewNovelParagraphReaction[];
  user_reaction?: string | null;
}

export interface ChapterReviewNovelSection {
  section_id: number;
  section_number: number;
  is_done?: boolean;
  section_type: 'novel';
  title: string;
  content?: {
    paragraphs?: ChapterReviewNovelParagraph[];
  } | null;
}

export type ChapterReviewAnySection = ChapterReviewSection | ChapterReviewNovelSection;

export interface ChapterReviewResponse {
  chapter_id: number;
  chapter_title: string;
  sections: ChapterReviewAnySection[];
  questions: ChapterReviewQuestion[];
  total_questions: number;
  is_premium?: boolean;
}

export interface ChapterReviewSubmitAnswerItem {
  section_id: number;
  question_id: string;
  selected_option_id: number; // 1-based or server ID
  time_spent?: number; // seconds
}

export interface ChapterReviewSubmitPayload {
  answers: ChapterReviewSubmitAnswerItem[];
}

export interface ChapterReviewSubmitResponse {
  success: boolean;
  message: string;
  accuracy_percentage: number;
  total_time_spent: string; // mm:ss
  correct_answers: number;
  total_questions: number;
}

export const getChapterReview = async (chapterId: number): Promise<ChapterReviewResponse> => {
  const res = await bookServiceApi.get<ChapterReviewResponse>(`/chapters/review/${chapterId}`);
  if (res.success && res.data) return res.data;
  throw new Error(res.error || 'Failed to fetch chapter review');
};

export const submitChapterReview = async (
  payload: ChapterReviewSubmitPayload
): Promise<ChapterReviewSubmitResponse> => {
  const res = await bookServiceApi.post<ChapterReviewSubmitResponse>(`/chapters/review/submit`, payload);
  if (res.success && res.data) return res.data;
  throw new Error(res.error || 'Failed to submit chapter review');
};
